import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import ActionButton from "../account/ActionButton";
import React from "react";
import { FormPaymentRequest } from "./types";

interface EnterCardDetailsStepProps {
  readonly form: FormPaymentRequest;
  readonly setForm: (f: FormPaymentRequest) => void;
  readonly onNext: () => void;
  readonly onBack: () => void;
}

export default function EnterCardDetailsStep({
  form,
  setForm,
  onNext,
  onBack,
}: EnterCardDetailsStepProps) {
  const [errors, setErrors] = React.useState<Record<string, string>>({});

  // Helper function to validate expiry date (month + year together)
  const validateExpiryDate = (updatedForm: FormPaymentRequest): string => {
    if (!updatedForm.expiryMonth || !updatedForm.expiryYear) return "";

    const month = parseInt(updatedForm.expiryMonth, 10);
    const year = parseInt(updatedForm.expiryYear, 10);
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;

    if (year < currentYear || (year === currentYear && month < currentMonth)) {
      return "Card has expired";
    }
    return "";
  };

  // Helper function to validate start date (month + year together)
  const validateStartDate = (updatedForm: FormPaymentRequest): string => {
    if (!updatedForm.startMonth || !updatedForm.startYear) return "";

    const month = parseInt(updatedForm.startMonth, 10);
    const year = parseInt(updatedForm.startYear, 10);
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;

    if (year > currentYear || (year === currentYear && month > currentMonth)) {
      return "Start date cannot be in the future";
    }
    return "";
  };

  const validateCardNumber = (value: string): string => {
    if (!value) return "Card number is required";
    if (!/^\d+$/.test(value)) return "Card number must contain only digits";
    if (value.length < 13 || value.length > 19)
      return "Card number must be between 13-19 digits";
    if (!isValidCardNumber(value)) return "Invalid card number";
    return "";
  };

  const validateExpiryMonth = (value: string): string => {
    if (!value) return "Expiry month is required";
    const month = parseInt(value, 10);
    if (isNaN(month) || month < 1 || month > 12)
      return "Month must be between 01-12";
    return "";
  };

  const validateExpiryYear = (value: string): string => {
    if (!value) return "Expiry year is required";
    const year = parseInt(value, 10);
    const currentYear = new Date().getFullYear();
    if (isNaN(year) || year < currentYear || year > currentYear + 20) {
      return `Year must be between ${currentYear}-${currentYear + 20}`;
    }
    return "";
  };

  const validateStartMonth = (value: string): string => {
    if (value && (parseInt(value, 10) < 1 || parseInt(value, 10) > 12)) {
      return "Month must be between 01-12";
    }
    return "";
  };

  const validateStartYear = (value: string): string => {
    if (value) {
      const year = parseInt(value, 10);
      const currentYear = new Date().getFullYear();
      if (isNaN(year) || year < currentYear - 50 || year > currentYear) {
        return `Year must be between ${currentYear - 50}-${currentYear}`;
      }
    }
    return "";
  };

  const validateSecurityCode = (value: string): string => {
    if (!value) return "Security code is required";
    if (!/^\d{3,4}$/.test(value)) return "Security code must be 3-4 digits";
    return "";
  };

  const validateName = (value: string): string => {
    if (!value) return "Name on card is required";
    if (value.length < 2) return "Name must be at least 2 characters";
    if (!/^[a-zA-Z\s'-]+$/.test(value))
      return "Name contains invalid characters";
    return "";
  };

  const validatePostcode = (value: string): string => {
    if (!value) return "Postcode is required";
    // NOTE: This regex is a simplified version for UK postcodes
    // It doesn't actually validate that the postcode exists
    // But this is just for frontend validation
    const ukPostcodeRegex = /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i;
    if (!ukPostcodeRegex.test(value.replace(/\s/g, ""))) {
      return "Please enter a valid UK postcode";
    }
    return "";
  };

  const validateAddress = (value: string): string => {
    if (!value) return "Address is required";
    if (value.length < 5) return "Address must be at least 5 characters";
    return "";
  };

  const validateField = (name: string, value: string): string => {
    switch (name) {
      case "cardNumber":
        return validateCardNumber(value);
      case "expiryMonth":
        return validateExpiryMonth(value);
      case "expiryYear":
        return validateExpiryYear(value);
      case "startMonth":
        return validateStartMonth(value);
      case "startYear":
        return validateStartYear(value);
      case "securityCode":
        return validateSecurityCode(value);
      case "name":
        return validateName(value);
      case "postcode":
        return validatePostcode(value);
      case "address":
        return validateAddress(value);
      default:
        return "";
    }
  };

  const isValidCardNumber = (cardNumber: string): boolean => {
    // Luhn algorithm - https://en.wikipedia.org/wiki/Luhn_algorithm
    let sum = 0;
    let shouldDouble = false;

    for (let i = cardNumber.length - 1; i >= 0; i--) {
      let digit = parseInt(cardNumber.charAt(i), 10);

      if (shouldDouble) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }

      sum += digit;
      shouldDouble = !shouldDouble;
    }

    return sum % 10 === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    let formattedValue = value;

    // Apply specific formatting based on field type
    switch (name) {
      case "cardNumber":
        // Only allow digits for the card number
        formattedValue = value.replace(/\D/g, "").slice(0, 19);
        break;
      case "expiryMonth":
      case "startMonth":
        // Ensure 2-digit month format
        formattedValue = value.replace(/\D/g, "").slice(0, 2);
        if (formattedValue.length === 1 && parseInt(formattedValue) > 1) {
          formattedValue = "0" + formattedValue;
        }
        break;
      case "expiryYear":
      case "startYear":
        // Only allow digits for year
        formattedValue = value.replace(/\D/g, "").slice(0, 4);
        break;
      case "securityCode":
        // Only allow digits for CVV
        formattedValue = value.replace(/\D/g, "").slice(0, 4);
        break;
      case "postcode":
        // Format UK postcode (uppercase, add space if needed)
        formattedValue = value.toUpperCase().replace(/\s/g, "");
        if (formattedValue.length > 3) {
          formattedValue =
            formattedValue.slice(0, -3) + " " + formattedValue.slice(-3);
        }
        break;
    }

    // Update form
    const updatedForm = {
      ...form,
      [name]: formattedValue,
    };
    setForm(updatedForm);

    // Validate the current field
    const error = validateField(name, formattedValue);
    const newErrors = {
      ...errors,
      [name]: error,
    };

    // Re-validate related fields for month/year combos
    if (
      (name === "expiryMonth" || name === "expiryYear") &&
      updatedForm.expiryMonth &&
      updatedForm.expiryYear
    ) {
      newErrors.expiryMonth = validateExpiryMonth(updatedForm.expiryMonth);
      newErrors.expiryYear = validateExpiryYear(updatedForm.expiryYear);
      const expiryDateError = validateExpiryDate(updatedForm);
      if (expiryDateError) {
        newErrors.expiryYear = expiryDateError;
      }
    } else if (
      (name === "startMonth" || name === "startYear") &&
      updatedForm.startMonth &&
      updatedForm.startYear
    ) {
      newErrors.startMonth = validateStartMonth(updatedForm.startMonth);
      newErrors.startYear = validateStartYear(updatedForm.startYear);
      const startDateError = validateStartDate(updatedForm);
      if (startDateError) {
        newErrors.startYear = startDateError;
      }
    }

    setErrors(newErrors);
  };

  const isFormValid = (): boolean => {
    const requiredFields = [
      "cardNumber",
      "expiryMonth",
      "expiryYear",
      "securityCode",
      "name",
      "postcode",
      "address",
    ];

    // Check if all required fields have values
    const hasAllValues = requiredFields.every(
      (field) => form[field as keyof typeof form]
    );

    // Check if there are any validation errors
    const hasErrors = Object.values(errors).some((error) => error !== "");

    return hasAllValues && !hasErrors;
  };
  return (
    <Paper sx={{ p: 3, mb: 2 }}>
      <Typography variant='h6' gutterBottom>
        Enter Card Details
      </Typography>
      <Box display='flex' flexDirection='column' gap={2}>
        <TextField
          name='cardNumber'
          label='Card Number'
          placeholder='1234 5678 9012 3456'
          value={form.cardNumber}
          onChange={handleChange}
          error={!!errors.cardNumber}
          helperText={errors.cardNumber}
          inputProps={{ maxLength: 19 }} // 19 digits
        />
        <Box display='flex' gap={2}>
          <TextField
            name='expiryMonth'
            label='Expiry Month'
            placeholder='MM'
            value={form.expiryMonth}
            onChange={handleChange}
            error={!!errors.expiryMonth}
            helperText={errors.expiryMonth}
            inputProps={{ maxLength: 2 }}
            sx={{ flex: 1 }}
          />
          <TextField
            name='expiryYear'
            label='Expiry Year'
            placeholder='YYYY'
            value={form.expiryYear}
            onChange={handleChange}
            error={!!errors.expiryYear}
            helperText={errors.expiryYear}
            inputProps={{ maxLength: 4 }}
            sx={{ flex: 1 }}
          />
        </Box>
        <Box display='flex' gap={2}>
          <TextField
            name='startMonth'
            label='Start Month (Optional)'
            placeholder='MM'
            value={form.startMonth}
            onChange={handleChange}
            error={!!errors.startMonth}
            helperText={errors.startMonth}
            inputProps={{ maxLength: 2 }}
            sx={{ flex: 1 }}
          />
          <TextField
            name='startYear'
            label='Start Year (Optional)'
            placeholder='YYYY'
            value={form.startYear}
            onChange={handleChange}
            error={!!errors.startYear}
            helperText={errors.startYear}
            inputProps={{ maxLength: 4 }}
            sx={{ flex: 1 }}
          />
        </Box>
        <TextField
          name='securityCode'
          label='Security Code (CVV)'
          placeholder='123'
          value={form.securityCode}
          onChange={handleChange}
          error={!!errors.securityCode}
          helperText={errors.securityCode}
          inputProps={{ maxLength: 4 }}
          type='password'
        />
        <TextField
          name='name'
          label='Name on Card'
          placeholder='John Smith'
          value={form.name}
          onChange={handleChange}
          error={!!errors.name}
          helperText={errors.name}
        />
        <TextField
          name='postcode'
          label='Postcode'
          placeholder='SW1A 1AA'
          value={form.postcode}
          onChange={handleChange}
          error={!!errors.postcode}
          helperText={errors.postcode}
        />
        <TextField
          name='address'
          label='Address'
          placeholder='123 Main Street, City'
          value={form.address}
          onChange={handleChange}
          error={!!errors.address}
          helperText={errors.address}
          multiline
          rows={2}
        />
        <Box
          sx={{
            pt: 4,
            textAlign: { xs: "center", md: "left" },
            display: "flex",
            flexDirection: "row",
          }}>
          <Box mt={2} mr={2}>
            <ActionButton
              sx={{ width: "150px" }}
              id='back-btn'
              borderColor='primary.dark'
              buttonColor='transparent'
              buttonTextColor='primary.dark'
              buttonTitle='Back'
              variant='outlined'
              hoverColor='primary.dark'
              hoverBorderColor={"primary.dark"}
              hoverTextColor='secondary.contrastText'
              onClick={onBack}
            />
          </Box>
          <Box mt={2}>
            <ActionButton
              sx={{ width: "150px" }}
              id='continue-btn'
              variant='contained'
              buttonColor='secondary.main'
              buttonTextColor='primary.contrastText'
              borderColor='transparent'
              hoverColor='primary.contrastText'
              hoverBorderColor='secondary.main'
              hoverTextColor='secondary.main'
              buttonTitle='Continue'
              disabled={!isFormValid()}
              onClick={onNext}
            />
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}
